/**
 * Column visibility picker modal for the Paid Media dashboard.
 *
 * Allows users to choose which data columns are visible in the dashboard table.
 * Always-visible columns (e.g., campaign name) cannot be unchecked. Provides
 * Select All / Deselect All shortcuts and shows a running count of selected columns.
 * The parent controls visibility by mounting/unmounting this component.
 */
import React, { useMemo, useState } from 'react';
import Button from '@amzn/meridian/button';
import Checkbox from '@amzn/meridian/checkbox';
import Column from '@amzn/meridian/column';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import { type PaidMediaColumnDef } from 'src/store/leela-api';

export interface ColumnPickerModalProps {
  readonly columns: readonly PaidMediaColumnDef[];
  readonly alwaysVisibleKeys: readonly string[];
  readonly selectedKeys: readonly string[] | null;
  readonly onConfirm: (selectedKeys: readonly string[]) => void;
  readonly onCancel: () => void;
}

export const ColumnPickerModal: React.FC<ColumnPickerModalProps> = ({
  columns,
  alwaysVisibleKeys,
  selectedKeys: selectedKeysProp,
  onConfirm,
  onCancel,
}): React.ReactElement => {
  const allKeys = useMemo(() => columns.map((c) => c.key), [columns]);

  const alwaysVisibleSet = new Set(alwaysVisibleKeys);
  const toggleableKeys = allKeys.filter((key) => !alwaysVisibleSet.has(key));

  const [selected, setSelected] = useState<readonly string[]>(
    () => selectedKeysProp ?? allKeys,
  );

  const effectiveSelected = useMemo(
    () => new Set<string>([...selected, ...alwaysVisibleKeys]),
    [selected, alwaysVisibleKeys],
  );

  // Count against rendered columns — selectedKeys may name keys the caller no longer offers.
  const selectedVisibleCount = columns.filter((c) => effectiveSelected.has(c.key)).length;

  const handleToggle = (key: string, checked: boolean) => {
    setSelected((prev) =>
      checked ? [...prev, key] : prev.filter((k) => k !== key),
    );
  };

  const handleSelectAll = () => {
    setSelected((prev) => {
      const currentSet = new Set(prev);
      for (const key of toggleableKeys) {
        currentSet.add(key);
      }
      return Array.from(currentSet);
    });
  };

  const handleDeselectAll = () => {
    setSelected((prev) => prev.filter((key) => alwaysVisibleSet.has(key)));
  };

  const handleConfirm = () => {
    onConfirm(Array.from(effectiveSelected));
  };

  return (
    <Modal
      open
      scrollContainer="modal"
      title="Customize Columns"
      onClose={onCancel}
    >
      <Column spacing="300">
        <Row spacing="200">
          <Button size="small" type="link" onClick={handleSelectAll}>
            Select All
          </Button>
          <Button size="small" type="link" onClick={handleDeselectAll}>
            Deselect All
          </Button>
        </Row>

        <Column spacing="200">
          {columns.map((column) => {
            const isAlwaysVisible = alwaysVisibleSet.has(column.key);

            return (
              <Checkbox
                checked={effectiveSelected.has(column.key)}
                disabled={isAlwaysVisible}
                key={column.key}
                onChange={(checked: boolean) => {
                  handleToggle(column.key, checked);
                }}
              >
                {column.label}
              </Checkbox>
            );
          })}
        </Column>
      </Column>

      <ModalFooter>
        <Row alignmentHorizontal="end" spacing="200" widths="fit">
          <Text type="b200">
            {selectedVisibleCount} of {columns.length} columns selected
          </Text>
          <Button type="tertiary" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="primary" onClick={handleConfirm}>
            Confirm
          </Button>
        </Row>
      </ModalFooter>
    </Modal>
  );
};
